import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Motorcycle, Package, ForkKnife, Clock, CaretRight } from '@phosphor-icons/react';
import { getResolvedMenu, defaultVariant } from '@/services/catalog';
import * as engine from '@/lib/ordering/cartEngine';
import ProductCard from '@/components/catalog/ProductCard';
import ProductCustomizer from '@/components/catalog/ProductCustomizer';
import { useCustomer, PageWrap } from './CustomerApp';
import { toast } from 'sonner';

const HERO = 'https://images.pexels.com/photos/18437684/pexels-photo-18437684.jpeg?auto=compress&cs=tinysrgb&w=1200';

export default function CustomerHome() {
  const navigate = useNavigate();
  const { selectedStore, cart, addToCart, updateCartQty } = useCustomer();
  const [menu, setMenu] = useState(null);
  const [orderType, setOrderType] = useState('DELIVERY');
  const [loading, setLoading] = useState(true);
  const [customizing, setCustomizing] = useState(null);

  useEffect(() => {
    if (!selectedStore) return;
    load();
    // Refresh periodically so a new offer, menu change, or price update
    // that staff makes while a customer already has this page open still
    // shows up — without requiring them to reload or navigate away and back.
    const interval = setInterval(load, 3 * 60 * 1000); // every 3 minutes
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedStore]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getResolvedMenu(selectedStore.id, 'online');
      setMenu(res.data);
    } catch { toast.error('Failed to load menu'); }
    finally { setLoading(false); }
  };

  const collections = menu?.collections || [];
  const categories = menu?.categories || [];
  const productQty = (p) => cart.filter((c) => c.menu_item_id === p.id).reduce((s, c) => s + c.qty, 0);

  const buildSimple = (product, qty = 1) => {
    const v = defaultVariant(product);
    return engine.createCartItem(
      { id: product.id, name: product.name, price: product.effective_price ?? v?.price ?? 0, image_url: product.image_url },
      { qty, variant: v ? { id: v.id, name: v.name, price: v.price } : null, modifiers: [] }
    );
  };
  const inc = (product) => {
    const it = buildSimple(product);
    const ex = cart.find((c) => c.signature === it.signature);
    updateCartQty(it.signature, (ex?.qty || 0) + 1);
  };
  const dec = (product) => {
    const it = buildSimple(product);
    const ex = cart.find((c) => c.signature === it.signature);
    if (ex) updateCartQty(it.signature, ex.qty - 1);
  };

  const quickAdd = (product) => {
    addToCart(buildSimple(product, 1));
    toast.success(`${product.name} added`, { duration: 1200 });
  };

  const confirmCustomize = (opts) => {
    const p = customizing;
    addToCart(engine.createCartItem({ id: p.id, name: p.name, price: p.effective_price ?? 0, image_url: p.image_url }, opts));
    setCustomizing(null);
    toast.success(`${p.name} added`, { duration: 1200 });
  };

  const orderTypes = [
    { type: 'DELIVERY', icon: Motorcycle, label: 'Delivery', sub: 'NOW' },
    { type: 'TAKEAWAY', icon: Package, label: 'Takeaway', sub: 'Pickup' },
    { type: 'DINE_IN', icon: ForkKnife, label: 'Dine-in', sub: 'At store' },
  ];

  return (
    <PageWrap>
      <div className="min-h-screen bg-[#FAFAFA] pb-28" data-testid="home-page">
        <div className="max-w-5xl mx-auto">
          {/* Store bar */}
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center justify-between gap-2 bg-white border border-gray-100 shadow-sm rounded-2xl px-4 py-3">
              <div className="flex items-start gap-2 min-w-0">
                <MapPin size={18} weight="fill" className="text-sspice mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-sspice uppercase tracking-wide">Ordering from</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{selectedStore?.name || 'Select a store'}</p>
                  {selectedStore?.address && <p className="text-xs text-gray-500 truncate">{selectedStore.address}</p>}
                </div>
              </div>
              <button data-testid="change-store-btn" onClick={() => navigate('/order/select-store')} className="flex-shrink-0 px-3 py-1.5 bg-sspice hover:bg-sspice-hover text-white text-xs font-semibold rounded-full transition-colors">Change</button>
            </div>
          </div>

          {/* Hero */}
          <div className="px-4 pt-2">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-3xl overflow-hidden h-52 sm:h-64" data-testid="home-hero">
              <img src={HERO} alt="Handcrafted pizza" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10 max-w-md">
                <span className="inline-flex items-center gap-1 self-start bg-sspice text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-3">Fresh from the oven</span>
                <h1 className="font-heading text-white text-3xl sm:text-4xl font-extrabold leading-tight">Handcrafted pizzas,<br />delivered hot.</h1>
                <button data-testid="hero-order-now-btn" onClick={() => navigate('/order/menu')} className="self-start mt-4 px-6 py-3 bg-sspice hover:bg-sspice-hover text-white font-bold rounded-full transition-transform hover:scale-105 shadow-lg">Order Now</button>
              </div>
            </motion.div>
          </div>

          {/* Order type */}
          <div className="mx-4 mt-4 mb-5 bg-white border border-gray-100 shadow-sm rounded-2xl p-1.5 flex">
            {orderTypes.map(({ type, icon: Icon, label, sub }) => (
              <button key={type} data-testid={`home-ordertype-${type.toLowerCase()}`} onClick={() => setOrderType(type)} className={`flex-1 py-2.5 rounded-xl flex flex-col items-center gap-0.5 transition-all ${orderType === type ? 'bg-sspice text-white shadow' : 'hover:bg-gray-50'}`}>
                <Icon size={18} weight={orderType === type ? 'fill' : 'regular'} className={orderType === type ? 'text-white' : 'text-gray-500'} />
                <span className={`text-xs font-semibold ${orderType === type ? 'text-white' : 'text-gray-700'}`}>{label}</span>
                <span className={`text-[10px] ${orderType === type ? 'text-white/80' : 'text-gray-400'}`}>{sub}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-sspice border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <>
              {/* Categories */}
              {categories.length > 0 && (
                <div className="mb-7">
                  <h3 className="px-4 font-heading text-lg font-bold text-gray-900 mb-3">What are you craving for?</h3>
                  <div className="flex gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide">
                    {categories.map((cat, i) => (
                      <motion.button
                        key={cat.id} data-testid={`home-cat-${cat.id}`}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                        onClick={() => navigate(`/order/menu?category=${encodeURIComponent(cat.name)}`)}
                        className="flex flex-col items-center gap-2 flex-shrink-0 group"
                      >
                        <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-sm ring-1 ring-gray-100 group-hover:ring-sspice transition-all bg-gray-50">
                          {cat.image_url || cat.products?.[0]?.image_url ? (
                            <img src={cat.image_url || cat.products[0].image_url} alt={cat.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-sspice/40 font-bold text-2xl">{cat.name[0]}</div>
                          )}
                        </div>
                        <span className="text-[11px] font-semibold text-gray-700 text-center max-w-[80px] truncate">{cat.name}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Collections */}
              {collections.map((coll) => (
                <div key={coll.id} className="mb-7" data-testid={`collection-${coll.id}`}>
                  <div className="px-4 flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-gray-900">{coll.name}</h3>
                      {coll.subtitle && <p className="text-xs text-gray-400">{coll.subtitle}</p>}
                    </div>
                    <button onClick={() => navigate('/order/menu')} className="text-xs text-sspice font-semibold flex items-center gap-0.5">See all <CaretRight size={12} weight="bold" /></button>
                  </div>
                  <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                    {coll.products.map((p) => (
                      <div key={p.id} className="min-w-[190px] max-w-[190px] flex-shrink-0">
                        <ProductCard product={p} qty={productQty(p)} layout="grid" onAdd={quickAdd} onCustomize={setCustomizing} onInc={inc} onDec={dec} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {categories.length === 0 && collections.length === 0 && (
                <div className="text-center py-10 text-gray-400 text-sm" data-testid="home-empty">This store hasn't published its menu yet.</div>
              )}
            </>
          )}

          {/* Browse full menu */}
          <div className="px-4 mt-2">
            <button data-testid="browse-full-menu-btn" onClick={() => navigate('/order/menu')} className="w-full py-4 bg-sspice hover:bg-sspice-hover text-white font-bold rounded-2xl transition-transform hover:scale-[1.01] shadow-md">Browse Full Menu</button>
            <p className="text-center text-[11px] text-gray-400 mt-3 flex items-center justify-center gap-1"><Clock size={12} /> Freshly prepared • Avg. delivery 30–40 mins</p>
          </div>
        </div>

        {customizing && <ProductCustomizer product={customizing} onClose={() => setCustomizing(null)} onConfirm={confirmCustomize} />}
      </div>
    </PageWrap>
  );
}
